/**
 * 轻量 STOMP 1.2 客户端（基于原生 WebSocket，零 npm 依赖）
 * 服务端为 Spring WebSocket(STOMP)：端点 /service/api/v1/ws/chat?isAjax=1（网关开放 API 区）
 * 用例（客服工作台）：CONNECT → SUBSCRIBE /topic/session/{id} → 收 MESSAGE；SEND /app/chat/{id}/read
 *
 * 用法：
 *   import { createStompClient } from './useWs'
 *   const client = createStompClient(url)
 *   client.onConnect = () => { client.subscribe('/topic/session/123', frame => {...}); client.send('/app/chat/123/read', JSON.stringify({...})) }
 *   client.connect()
 *   client.disconnect()
 */

const HEARTBEAT_MS = 10000

function encodeFrame(command, headers, body) {
  let out = command + '\n'
  Object.keys(headers || {}).forEach(k => {
    out += k + ':' + String(headers[k]) + '\n'
  })
  out += '\n'
  if (body) out += body
  return out + '\0'
}

export function createStompClient(url) {
  let socket = null
  let subscribed = [] // [{destination, cb}]
  let heartbeatTimer = null
  let connected = false
  let closedByUser = false

  const client = {
    onConnect: null,
    onError: null,

    connect() {
      closedByUser = false
      socket = new WebSocket(url)
      socket.onopen = () => {
        socket.send(encodeFrame('CONNECT', {
          'accept-version': '1.2',
          host: 'localhost',
          'heart-beat': HEARTBEAT_MS + ',' + HEARTBEAT_MS
        }))
      }
      socket.onmessage = evt => handleFrame(evt.data)
      socket.onclose = () => {
        if (heartbeatTimer) { clearInterval(heartbeatTimer); heartbeatTimer = null }
        connected = false
        if (!closedByUser) {
          // 5s 重连
          setTimeout(() => client.connect(), 5000)
        }
      }
      socket.onerror = err => {
        if (client.onError) client.onError(err)
      }
    },

    subscribe(destination, cb) {
      subscribed.push({ destination, cb })
      if (connected) {
        socket.send(encodeFrame('SUBSCRIBE', {
          id: 'sub-' + subscribed.length,
          destination,
          ack: 'auto'
        }))
      }
    },

    send(destination, body) {
      if (!connected) return
      socket.send(encodeFrame('SEND', {
        destination,
        'content-type': 'application/json'
      }, body))
    },

    disconnect() {
      closedByUser = true
      if (socket && connected) {
        try { socket.send(encodeFrame('DISCONNECT', {})) } catch (e) { /* ignore */ }
        socket.close()
      }
    }
  }

  function handleFrame(raw) {
    if (!raw) return
    // 心跳帧：空白字符
    const trimmed = String(raw).replace(/^[\s]+/, '')
    if (trimmed === '' || trimmed === '\0') return
    // 协议级多帧可靠处理（一次消息内可能含多个 \0）
    const frames = String(raw).split('\0')
    frames.forEach(f => {
      if (!f || !f.trim()) return
      parseFrame(f)
    })
  }

  function parseFrame(text) {
    const sep = text.indexOf('\n\n')
    const head = sep === -1 ? text : text.slice(0, sep)
    let body = sep === -1 ? '' : text.slice(sep + 2)

    const lines = head.split('\n')
    const command = lines[0].trim()
    const headers = {}
    for (let i = 1; i < lines.length; i++) {
      const idx = lines[i].indexOf(':')
      if (idx > 0) headers[lines[i].slice(0, idx).trim()] = lines[i].slice(idx + 1).trim()
    }

    // 注意：Spring STOMP 的 content-length 是 UTF-8 字节数，不能按 JS 字符 slice（中文消息会被截断而 JSON 解析失败）
    // body 已在按 \0 拆帧时完整取得，直接使用，不做截断

    switch (command) {
      case 'CONNECTED': {
        connected = true
        heartbeatTimer = setInterval(() => {
          if (socket && socket.readyState === 1) socket.send('\n')
        }, HEARTBEAT_MS)
        // 重新订阅（重连场景）
        subscribed.forEach(s => {
          socket.send(encodeFrame('SUBSCRIBE', {
            id: 'sub-' + subscribed.indexOf(s) + 1,
            destination: s.destination,
            ack: 'auto'
          }))
        })
        if (client.onConnect) client.onConnect(client)
        break
      }
      case 'MESSAGE': {
        const destination = headers['destination']
        const sub = subscribed.find(s => s.destination === destination)
        if (sub && sub.cb) {
          let data = body
          try { data = JSON.parse(body) } catch (e) { /* 保留原文 */ }
          sub.cb({ headers, body, data })
        }
        break
      }
      case 'ERROR': {
        if (client.onError) client.onError({ headers, body })
        break
      }
      default:
        break
    }
  }

  return client
}